import type { AlterlyEffectInstance, HalftoneInstance } from "../effects/effectRegistry";
import type { MediaAsset } from "../media/mediaTypes";
import { AppError } from "../../shared/errors/AppError";
import { assertExportMemoryBudget, assertVectorComplexity } from "./exportSafety";
import type { ExportResult } from "./exportTypes";

function loadImage(url: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.decoding = "async";
    image.onload = () => resolve(image);
    image.onerror = () => reject(new AppError("EXPORT_FAILED", "Could not decode the source image for SVG export."));
    image.src = url;
  });
}

function baseName(name: string) {
  return name.replace(/\.[^/.]+$/, "") || "alterly-export";
}

function escapeAttribute(value: string) {
  return value.replaceAll("&", "&amp;").replaceAll('"', "&quot;").replaceAll("<", "&lt;");
}

export function getVectorExportEffect(effects: readonly AlterlyEffectInstance[]): HalftoneInstance | null {
  const enabled = effects.filter((effect) => effect.enabled && effect.opacity > 0);
  if (enabled.length !== 1 || enabled[0]?.definitionId !== "halftone") return null;
  return enabled[0];
}

export async function exportHalftoneSvg(
  asset: MediaAsset,
  effect: HalftoneInstance,
): Promise<ExportResult> {
  if (asset.kind !== "image") {
    throw new AppError("EXPORT_FAILED", "SVG export currently requires an image source.");
  }

  const { width, height } = asset.dimensions;
  const p = effect.parameters;
  const cell = Math.max(2, Math.round(p.cellSize));
  assertExportMemoryBudget(width, height, 2);
  assertVectorComplexity(width, height, cell);

  const image = await loadImage(asset.objectUrl);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) throw new AppError("EXPORT_FAILED", "Canvas 2D is unavailable for SVG sampling.");

  try {
    context.drawImage(image, 0, 0, width, height);
    const pixels = context.getImageData(0, 0, width, height).data;
    const radians = (p.angle * Math.PI) / 180;
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);
    const centerX = width / 2;
    const centerY = height / 2;
    const circles: string[] = [];

    for (let y = cell / 2; y < height; y += cell) {
      for (let x = cell / 2; x < width; x += cell) {
        const sx = Math.max(0, Math.min(width - 1, Math.round(x)));
        const sy = Math.max(0, Math.min(height - 1, Math.round(y)));
        const index = (sy * width + sx) * 4;
        const r = pixels[index] ?? 0;
        const g = pixels[index + 1] ?? 0;
        const b = pixels[index + 2] ?? 0;
        let luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
        luminance = Math.max(0, Math.min(1, luminance + p.threshold));
        if (!p.invert) luminance = 1 - luminance;

        const radius = Math.max(0, Math.min(cell * 0.75, luminance * (cell / 2) * p.dotGain));
        if (radius <= 0.05) continue;

        const dx = x - centerX;
        const dy = y - centerY;
        const cx = centerX + dx * cos - dy * sin;
        const cy = centerY + dx * sin + dy * cos;
        circles.push(`<circle cx="${cx.toFixed(2)}" cy="${cy.toFixed(2)}" r="${radius.toFixed(2)}"/>`);
      }
    }

    const svg = [
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}">`,
      `<title>${escapeAttribute(asset.name)} — Alterly Halftone</title>`,
      `<rect width="100%" height="100%" fill="${escapeAttribute(p.background)}"/>`,
      `<g fill="${escapeAttribute(p.foreground)}" opacity="${Math.max(0, Math.min(1, effect.opacity)).toFixed(3)}">`,
      ...circles,
      "</g>",
      "</svg>",
    ].join("");

    const blob = new Blob([svg], { type: "image/svg+xml" });
    return {
      blob,
      filename: `${baseName(asset.name)}-alterly-halftone.svg`,
      mimeType: "image/svg+xml",
    };
  } finally {
    canvas.width = 1;
    canvas.height = 1;
    image.src = "";
  }
}
